import { avatarVariants } from '../../components/Avatar';
import { BRAND_HEX, type BrandPalette } from '../../styles/brand-colors';
import { withAlpha } from '../../utils/color';
import type { AvatarProgressRingSize, AvatarRingTone } from './AvatarProgressRing.types';

/** Matches the Avatar size map (`l` 40 · `xl` 44 · `2xl` 48 · `4xl` 125). */
export const AVATAR_RING_DIAMETER: Record<AvatarProgressRingSize, number> = {
  l: 40,
  xl: 44,
  '2xl': 48,
  '4xl': 125,
};

/**
 * Ring thickness per size — a LOOKUP, deliberately not a formula.
 *
 * Both design sources are in the `New-IA — iPad Mobile` file
 * (`v4J1tu2TRhJNVVPRc5EI72`), and they do not share a ratio:
 *   - `xl`  — node `67:17095`, the top bar: annulus r=17.6→22 on a 44pt
 *     avatar, i.e. a 4.4 stroke on a 19.8 centreline. 10% of the diameter.
 *     · outer edge flush to the avatar.
 *   - `4xl` — node `114:46902`, the Profile hero: annulus r=53.125→62.5 on a
 *     125px avatar, i.e. a 9.375 stroke on a 57.8125 centreline. 7.5%.
 *
 * A thinner ring at the larger size is the designer's call, not an error — so
 * these are transcribed, never computed. `l` and `2xl` have no design node and
 * are interpolated from the `xl` anchor; re-measure them if a node appears.
 *
 * `AvatarProgressRing` derives the centreline as `(diameter - stroke) / 2`,
 * reproducing 19.8 at `xl` and 57.8125 at `4xl` exactly.
 */
export const AVATAR_RING_STROKE: Record<AvatarProgressRingSize, number> = {
  l: 4, // interpolated
  xl: 4.4, // design: node 67:17095
  '2xl': 4.8, // interpolated
  '4xl': 9.375, // design: node 114:46902
};

/**
 * Does the `Avatar` at this size grow with the responsive rem ramp?
 *
 * The Avatar size map deliberately mixes two units, and the ring has to follow
 * whichever one its size uses:
 *   - `l`/`xl`/`2xl` are Tailwind rem steps (`h-10`/`h-11`/`h-12`), and native
 *     ramps rem with WINDOW width (1.25× above 1024pt), so these GROW.
 *   - `4xl` is `h-[125px]` — an arbitrary-px hero measured off the design node.
 *     Arbitrary px does not ramp, so this one STAYS PUT.
 *
 * Scaling all four broke the Profile hero: a 1.25× ring around a fixed 125px
 * avatar left a visible gap between the avatar's edge and the ring's inner edge.
 *
 * READ OFF `avatarVariants` rather than transcribed, because a hand-copied
 * mirror of the Avatar size map is exactly what let `AVATAR_RING_DIAMETER`
 * drift (see its "Matches the Avatar size map" comment). Changing an Avatar
 * size's UNIT now moves the ring with it instead of leaving a stale table
 * behind. Memoised: the classes are static, so this resolves once per size.
 */
const _rampsBySize = new Map<AvatarProgressRingSize, boolean>();

function avatarSizeRamps(size: AvatarProgressRingSize): boolean {
  const cached = _rampsBySize.get(size);
  if (cached !== undefined) return cached;
  // `h-[125px]` = arbitrary px, pinned. `h-11` = a rem step, ramps.
  const ramps = !/\bh-\[\d+px\]/.test(avatarVariants({ size }));
  _rampsBySize.set(size, ramps);
  return ramps;
}

/**
 * The ring's drawn geometry at a given responsive scale.
 *
 * The tables above are POINTS, transcribed from Figma, and the ring is painted
 * as a raw-number SVG. The avatar beneath it is only sometimes raw — see
 * `AVATAR_SIZE_RAMPS`. Taking the scale on a rem-sized avatar is what keeps the
 * ring flush (a raw 44pt ring inside a 55pt avatar shipped on an iPad Pro 13");
 * NOT taking it on a px-sized one is what keeps the hero flush.
 *
 * Pure and exported for the same reason `isTabletSize` is: the component and its
 * spec share ONE definition of the geometry instead of each carrying its own.
 * `scale` is a REQUIRED param — defaulting it to 1 is precisely the first bug.
 */
export function avatarRingGeometry(size: AvatarProgressRingSize, scale: number) {
  // Same defence as the component's tone lookup: an unknown size would make the
  // geometry NaN and render an invisible ring with no error to point at.
  const effectiveScale = avatarSizeRamps(size) ? scale : 1;
  const diameter =
    (AVATAR_RING_DIAMETER[size] ?? AVATAR_RING_DIAMETER.xl) * effectiveScale;
  const stroke = (AVATAR_RING_STROKE[size] ?? AVATAR_RING_STROKE.xl) * effectiveScale;
  const radius = (diameter - stroke) / 2;

  return { diameter, stroke, radius, circumference: 2 * Math.PI * radius };
}

/** The brand palette each ring tone draws from — the DS `Badge` colour names. */
const TONE_PALETTE: Record<AvatarRingTone, BrandPalette> = {
  neutral: 'secondary',
  reward: 'amber',
  primary: 'primary',
  info: 'tertiary',
};

/**
 * One tone's track + fill, from its palette.
 *
 * The shape is the rule the original amber ring already encoded (Figma
 * `108:26929`): a `-50` track under a `-500` fill in light, and in dark a deep
 * `-950` track at 90% under a lighter `-400` fill, so the ring stays legible on
 * a dark surface (same approach as `SECTION_HEADER_CONTENT_COLOR`).
 *
 * DERIVED, not re-typed: `withAlpha(BRAND_HEX.amber['950'], 0.9)` returns
 * `'rgba(70, 25, 1, 0.9)'` — the previously hardcoded dark amber track,
 * character for character — so adding tones cannot have re-tuned the amber ring
 * that already shipped. A retuned palette shade now moves the ring with it
 * instead of leaving a stale hex behind.
 *
 * Raw colours rather than Tailwind classes because `react-native-svg`'s
 * `stroke` is a plain colour prop, not a styled attribute (same finding as
 * `StudyRing`'s `resolveStrokeColor`).
 */
const toneTokens = (tone: AvatarRingTone) => {
  const shades = BRAND_HEX[TONE_PALETTE[tone]];
  return {
    light: { track: shades['50'], fill: shades['500'] },
    dark: { track: withAlpha(shades['950'], 0.9), fill: shades['400'] },
  } as const;
};

/**
 * BREAKING (PR #384): this was `{ light, dark }` and is now keyed by tone
 * first — read `AVATAR_RING_TOKENS[tone].light`, not `AVATAR_RING_TOKENS.light`.
 * The old spelling now resolves to `undefined` rather than failing loudly, so
 * check here first if a ring ever paints nothing. No consumer outside this
 * block existed at the time of the change.
 *
 * Ring colours per tone and theme. The tone says WHICH PLAN the ring belongs to
 * — see `planStatusTone` in `@flashka/shared/credits/view`, the single table
 * that both this ring and the Profile plan badge read.
 */
export const AVATAR_RING_TOKENS: Record<AvatarRingTone, ReturnType<typeof toneTokens>> = {
  neutral: toneTokens('neutral'),
  reward: toneTokens('reward'),
  primary: toneTokens('primary'),
  info: toneTokens('info'),
};
